/**
 * ActionConfirm — consequence preview dialog for destructive/irreversible actions.
 *
 * Phase 8 Prompt 2 — per audit deliverable 04B Section E Action Consequence
 * Preview Catalog and 04D P4 (one-click reversibles, two-click destructives).
 *
 * Supports four confirmation patterns from 04B:
 *   - one-click            → simple Cancel/Confirm
 *   - type-to-confirm      → user must type a specific string (e.g. "DELETE")
 *   - reason-required      → user must provide reason text (logged to audit_log)
 *   - blocked-with-suggestion → action blocked; offers alternative (e.g. Archive)
 *
 * Copy is interpolated with the context prop so consequence previews reflect
 * the actual numbers ("This will affect 4,237 future enrollments.").
 *
 * Usage:
 *   <ActionConfirm
 *     actionId="delete_template"
 *     open={confirmOpen}
 *     context={{ trigger_count: 3, funnel_count: 1, future_send_count: 4237 }}
 *     onConfirm={(reason) => handleDelete(reason)}
 *     onCancel={() => setConfirmOpen(false)}
 *   />
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
  Box,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  useHelpContent,
  interpolateCopy,
} from "../../contexts/HelpContentContext";

export interface ActionConfirmProps {
  /** Action id — looks up content.actionConfirms[actionId]. */
  actionId: string;
  /** Whether the dialog is open. Parent controls state. */
  open: boolean;
  /** Variables to interpolate into the copy ({{trigger_count}} etc.). */
  context?: Record<string, string | number | undefined>;
  /**
   * Called when the user confirms.
   * - For "reason-required" the reason string is passed.
   * - For others the reason is undefined.
   */
  onConfirm: (reason?: string) => void;
  /** Called when the user cancels or closes the dialog. */
  onCancel: () => void;
  /**
   * Optional alternative-action handler used by "blocked-with-suggestion"
   * (e.g. clicking "Archive instead" on the delete_audience confirm).
   */
  onAlternative?: () => void;
  /** Label for the alternative action button (e.g. "Archive instead"). */
  alternativeLabel?: string;
}

export const ActionConfirm: React.FC<ActionConfirmProps> = ({
  actionId,
  open,
  context = {},
  onConfirm,
  onCancel,
  onAlternative,
  alternativeLabel = "Use alternative",
}) => {
  const { getActionConfirm } = useHelpContent();
  const entry = getActionConfirm(actionId);

  const [typedConfirm, setTypedConfirm] = useState("");
  const [reason, setReason] = useState("");

  // Reset inputs when dialog opens / actionId changes.
  useEffect(() => {
    if (open) {
      setTypedConfirm("");
      setReason("");
    }
  }, [open, actionId]);

  const interpolated = useMemo(
    () => (entry ? interpolateCopy(entry.copy, context) : ""),
    [entry, context],
  );

  if (!entry) {
    return null;
  }

  // Determine if the Confirm button should be enabled.
  let confirmEnabled = true;
  if (entry.confirmType === "type-to-confirm" && entry.confirmText) {
    confirmEnabled = typedConfirm.trim() === entry.confirmText;
  } else if (entry.confirmType === "reason-required") {
    confirmEnabled = reason.trim().length >= 4;
  } else if (entry.confirmType === "blocked-with-suggestion") {
    confirmEnabled = false; // primary action blocked; alternative offered
  }

  const handleConfirm = () => {
    if (!confirmEnabled) return;
    onConfirm(entry.confirmType === "reason-required" ? reason : undefined);
  };

  // Render the copy with **bold** markdown converted to <strong>.
  const renderedCopy = renderMarkdownBold(interpolated);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="action-confirm-title"
    >
      <DialogTitle
        id="action-confirm-title"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontFamily: '"Playfair Display", serif',
          color: entry.destructive ? "#7D1935" : "text.primary",
        }}
      >
        {entry.destructive && <WarningAmberIcon sx={{ color: "#D4AC0D" }} />}
        {entry.label}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
          {renderedCopy}
        </Typography>

        {entry.confirmType === "type-to-confirm" && entry.confirmText && (
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              Type <strong>{entry.confirmText}</strong> to confirm:
            </Typography>
            <TextField
              fullWidth
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder={entry.confirmText}
              autoFocus
              size="small"
            />
          </Box>
        )}

        {entry.confirmType === "reason-required" && (
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              Reason for change (required, logged to audit trail):
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you making this change?"
              autoFocus
            />
          </Box>
        )}

        {entry.confirmType === "blocked-with-suggestion" && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            This action is blocked. Use the alternative action below instead.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>

        {entry.confirmType === "blocked-with-suggestion" && onAlternative && (
          <Button
            onClick={onAlternative}
            variant="contained"
            sx={{
              backgroundColor: "#1A5F7A",
              "&:hover": { backgroundColor: "#154D62" },
            }}
          >
            {alternativeLabel}
          </Button>
        )}

        <Button
          onClick={handleConfirm}
          disabled={!confirmEnabled}
          variant="contained"
          sx={{
            backgroundColor: entry.destructive ? "#7D1935" : "#D4AC0D",
            color: entry.destructive ? "#FFFFFF" : "#1B1F23",
            "&:hover": {
              backgroundColor: entry.destructive ? "#5C0F26" : "#B89309",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E0D2",
              color: "#B4BCC5",
            },
          }}
        >
          {entry.label}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Render a small subset of markdown — only **bold** (the only marker the
 * registry uses). Returns a React fragment.
 */
function renderMarkdownBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

export default ActionConfirm;
